'use client'

import SalesTasksPanel from '@/components/sales/SalesTasksPanel'
import AtendimentoChatPage from '@/pages/admin/atendimento/atendimentos/atendimento-chat-page'
import { ChatChannelIcon } from '@/components/atendimento/chat-channel-icon'
import { ChatPopupProvider } from '@/components/chat-popup'
import { useServiceLabel } from '@/lib/hooks/use-service-label'
import ConfirmModal from '@/components/confirm-modal'
import DealItemEditor from '@/components/sales/DealItemEditor'
import {
    addDealItem,
    closeDeal,
    CloseDealData,
    CreateDealItemData,
    Deal,
    DEAL_STATUS_COLORS,
    DEAL_STATUS_LABELS,
    deleteDeal,
    getDeal,
    moveDealStage,
    removeDealItem,
    updateDeal,
    UpdateDealData
} from '@/lib/api/sales/deals-api'
import {
    getPipeline,
    SalesPipeline
} from '@/lib/api/sales/pipelines-api'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useAuth } from '@/store/hooks'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useEffect, useMemo, useState } from 'react'
import {
    MdAdd,
    MdArrowBack,
    MdArrowForward,
    MdArrowRightAlt,
    MdAttachMoney,
    MdChat,
    MdCheckCircle,
    MdDelete,
    MdEdit,
    MdPerson,
    MdPlayArrow,
    MdTimeline,
    MdMedicalServices,
    MdReceipt,
    MdBiotech,
    MdVideocam,
} from 'react-icons/md'
import { CgEye } from 'react-icons/cg'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

type TabKey = 'details' | 'lead' | 'history' | 'conversations' | 'tasks' | 'appointments' | 'billings'

const APPOINTMENT_STATUS_LABELS: Record<string, string> = {
    // Consultation
    SCHEDULED: 'Agendada',
    WAITING: 'Aguardando',
    IN_PROGRESS: 'Em Andamento',
    COMPLETED: 'Concluída',
    CANCELLED: 'Cancelada',
    NO_SHOW: 'Não Compareceu',
    // Exam
    REQUESTED: 'Solicitado',
    // Order
    POSTPONED: 'Adiado',
    // Teleconsultation
    CREATED: 'Criada',
    WAITING_PATIENT: 'Aguardando Paciente',
    WAITING_DOCTOR: 'Aguardando Profissional',
    EXPIRED: 'Expirada',
    // Billing
    PENDING: 'Pendente',
    PAID: 'Paga',
    OVERDUE: 'Vencida',
    REFUNDED: 'Reembolsada',
    PARTIALLY_PAID: 'Parcialmente Paga',
    IN_DISPUTE: 'Em Disputa',
    WRITTEN_OFF: 'Baixada',
}

const STATUS_COLORS: Record<string, string> = {
    SCHEDULED: 'badge-info',
    WAITING: 'badge-warning',
    IN_PROGRESS: 'badge-info',
    COMPLETED: 'badge-success',
    CANCELLED: 'badge-ghost',
    NO_SHOW: 'badge-error',
    REQUESTED: 'badge-warning',
    POSTPONED: 'badge-warning',
    CREATED: 'badge-info',
    WAITING_PATIENT: 'badge-warning',
    WAITING_DOCTOR: 'badge-warning',
    EXPIRED: 'badge-ghost',
    PENDING: 'badge-warning',
    PAID: 'badge-success',
    OVERDUE: 'badge-error',
    REFUNDED: 'badge-info',
    PARTIALLY_PAID: 'badge-warning',
    IN_DISPUTE: 'badge-error',
    WRITTEN_OFF: 'badge-ghost',
}

interface DealDetailPageProps {
    idOverride?: number
    onBack?: () => void
}

export default function DealDetailPage({ idOverride, onBack }: DealDetailPageProps = {}) {
    const { user } = useAuth()
    const serviceLabels = useServiceLabel()
    const navigate = useNavigate()
    const { id: paramId } = useParams<{ id: string }>()
    const id = idOverride ? String(idOverride) : paramId
    /** No modal, sair da lista significa fechar; na rota, navegar. */
    const goBack = () => (onBack ? onBack() : navigate('/sales/deals'))
    const { canUpdate, canDelete } = useResourcePermissions('sales', 'deals')

    const [deal, setDeal] = useState<Deal | null>(null)
    const [pipeline, setPipeline] = useState<SalesPipeline | null>(null)
    const [loading, setLoading] = useState(true)
    const [activeTab, setActiveTab] = useState<TabKey>('details')
    // Conversa aberta na aba "Conversas"; vazio = a da oportunidade (ou a mais recente do lead)
    const [selectedChatId, setSelectedChatId] = useState<number | null>(null)

    // Move stage
    const [stageToMove, setStageToMove] = useState<{ id: number; name: string } | null>(null)
    const [moving, setMoving] = useState(false)

    // Close deal
    const [showCloseModal, setShowCloseModal] = useState(false)
    const [closeForm, setCloseForm] = useState<CloseDealData>({ status: 'WON' })
    const [closing, setClosing] = useState(false)

    // Edit deal
    const [showEditModal, setShowEditModal] = useState(false)
    const [editForm, setEditForm] = useState<UpdateDealData>({})
    const [saving, setSaving] = useState(false)

    // Delete deal
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [deleting, setDeleting] = useState(false)

    // Add item
    const [showAddItemModal, setShowAddItemModal] = useState(false)
    const [itemForm, setItemForm] = useState<CreateDealItemData>({ name: '', unitPrice: 0 })
    const [addingItem, setAddingItem] = useState(false)

    const resetItemForm = () => {
        setItemForm({ name: '', unitPrice: 0 })
    }

    // Remove item
    const [showRemoveItemModal, setShowRemoveItemModal] = useState(false)
    const [itemToRemove, setItemToRemove] = useState<number | null>(null)
    const [removingItem, setRemovingItem] = useState(false)

    useEffect(() => {
        if (id) loadDeal()
    }, [id])

    useEffect(() => {
        if (deal?.pipelineId) loadPipeline(deal.pipelineId)
    }, [deal?.pipelineId])

    const loadDeal = async () => {
        if (!id) return
        setLoading(true)
        try {
            const response = await getDeal(parseInt(id, 10))
            if (response.success && response.result) {
                setDeal(response.result)
            } else {
                toast.error(response.error || 'Erro ao carregar oportunidade')
                goBack()
            }
        } catch {
            toast.error('Erro ao carregar oportunidade')
            goBack()
        } finally {
            setLoading(false)
        }
    }

    const loadPipeline = async (pipelineId: number) => {
        try {
            const response = await getPipeline(pipelineId)
            if (response.success && response.result) {
                setPipeline(response.result)
            }
        } catch {
            // Silently fail
        }
    }

    const stages = useMemo(() => {
        return (pipeline?.stages || []).sort((a, b) => a.sortOrder - b.sortOrder)
    }, [pipeline])

    const formatBRL = (cents: number) =>
        new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(cents / 100)

    const handleMoveStage = async () => {
        if (!id || !deal || !stageToMove) return
        setMoving(true)
        try {
            const response = await moveDealStage(parseInt(id, 10), { stageId: stageToMove.id })
            if (response.success) {
                toast.success('Oportunidade movida de etapa')
                setStageToMove(null)
                loadDeal()
            } else {
                toast.error(response.error || 'Erro ao mover etapa')
            }
        } catch {
            toast.error('Erro ao mover etapa')
        } finally {
            setMoving(false)
        }
    }

    const handleClose = async () => {
        if (!id) return
        setClosing(true)
        try {
            const response = await closeDeal(parseInt(id, 10), closeForm)
            if (response.success) {
                toast.success('Oportunidade fechada com sucesso')
                setShowCloseModal(false)
                loadDeal()
            } else {
                toast.error(response.error || 'Erro ao fechar oportunidade')
            }
        } catch {
            toast.error('Erro ao fechar oportunidade')
        } finally {
            setClosing(false)
        }
    }

    const handleEdit = async () => {
        if (!id) return
        setSaving(true)
        try {
            const response = await updateDeal(parseInt(id, 10), editForm)
            if (response.success) {
                toast.success('Oportunidade atualizada')
                setShowEditModal(false)
                loadDeal()
            } else {
                toast.error(response.error || 'Erro ao atualizar')
            }
        } catch {
            toast.error('Erro ao atualizar oportunidade')
        } finally {
            setSaving(false)
        }
    }

    const handleDelete = async () => {
        if (!id) return
        setDeleting(true)
        try {
            const response = await deleteDeal(parseInt(id, 10))
            if (response.success) {
                toast.success('Oportunidade removida')
                goBack()
            } else {
                toast.error(response.error || 'Erro ao remover')
            }
        } catch {
            toast.error('Erro ao remover oportunidade')
        } finally {
            setDeleting(false)
        }
    }

    const handleAddItem = async () => {
        if (!id || !itemForm.name || !itemForm.unitPrice) {
            toast.error('Preencha nome e preço unitário')
            return
        }
        setAddingItem(true)
        try {
            const response = await addDealItem(parseInt(id, 10), itemForm)
            if (response.success) {
                toast.success('Item adicionado')
                setShowAddItemModal(false)
                setItemForm({ name: '', unitPrice: 0 })
                loadDeal()
            } else {
                toast.error(response.error || 'Erro ao adicionar item')
            }
        } catch {
            toast.error('Erro ao adicionar item')
        } finally {
            setAddingItem(false)
        }
    }

    const handleRemoveItem = async () => {
        if (!id || !itemToRemove) return
        setRemovingItem(true)
        try {
            const response = await removeDealItem(parseInt(id, 10), itemToRemove)
            if (response.success) {
                toast.success('Item removido')
                setShowRemoveItemModal(false)
                setItemToRemove(null)
                loadDeal()
            } else {
                toast.error(response.error || 'Erro ao remover item')
            }
        } catch {
            toast.error('Erro ao remover item')
        } finally {
            setRemovingItem(false)
        }
    }

    const openEditModal = () => {
        if (!deal) return
        setEditForm({
            title: deal.title,
            description: deal.description || '',
            expectedCloseDate: deal.expectedCloseDate?.substring(0, 10) || '',
            tags: deal.tags || [],
        })
        setShowEditModal(true)
    }

    if (loading) {
        return (
            <div className="p-6 space-y-4">
                <div className="skeleton h-8 w-48" />
                <div className="skeleton h-32 w-full" />
                <div className="skeleton h-64 w-full" />
            </div>
        )
    }

    if (!deal) {
        return (
            <div className="p-6">
                <div className="alert alert-error">Oportunidade não encontrada</div>
            </div>
        )
    }

    const itemsTotal = (deal.items || []).reduce(
        (sum, item) => sum + (item.quantity * item.unitPrice - item.discount),
        0
    )

    return (
        <ChatPopupProvider>
            <div className="p-6 space-y-6">
                {/* Header */}
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                    <div className="flex items-start gap-4">
                        <button
                            className="btn btn-ghost btn-sm mt-1"
                            onClick={() => goBack()}
                        >
                            <MdArrowBack size={20} />
                        </button>
                        <div>
                            <div className="flex items-center gap-3 flex-wrap">
                                <h1 className="text-2xl font-bold">{deal.title}</h1>
                                <span className={`badge ${DEAL_STATUS_COLORS[deal.status]}`}>
                                    {DEAL_STATUS_LABELS[deal.status]}
                                </span>
                                <span className="text-xl font-bold text-secondary">
                                    {formatBRL(deal.totalValue || 0)}
                                </span>
                            </div>
                            {/* Breadcrumb: Pipeline > Stage */}
                            <div className="flex items-center gap-2 mt-2 text-sm">
                                <span className="opacity-70">{deal.pipeline?.name}</span>
                                <MdArrowRightAlt size={16} className="opacity-40" />
                                <span
                                    className="font-medium px-2 py-0.5 rounded"
                                    style={{
                                        backgroundColor: deal.stage?.color || '#888',
                                        color: '#fff',
                                    }}
                                >
                                    {deal.stage?.name}
                                </span>
                                {deal.assignedUser && (
                                    <span className="flex items-center gap-1 ml-4 opacity-70">
                                        <MdPerson size={14} /> {deal.assignedUser.name}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                    {canUpdate && deal.status === 'OPEN' && (
                        <div className="flex gap-2 flex-wrap">
                            <button
                                className="btn btn-sm btn-success"
                                onClick={() => {
                                    setCloseForm({ status: 'WON' })
                                    setShowCloseModal(true)
                                }}
                            >
                                <MdCheckCircle size={16} /> Fechar
                            </button>
                            <button className="btn btn-sm btn-outline" onClick={openEditModal}>
                                <MdEdit size={16} /> Editar
                            </button>
                            {canDelete && (
                                <button
                                    className="btn btn-sm btn-error btn-outline"
                                    onClick={() => setShowDeleteModal(true)}
                                >
                                    <MdDelete size={16} />
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {/* Stage progress bar */}
                {deal.status === 'OPEN' && stages.length > 0 && (
                    <div className="flex items-center gap-1 overflow-x-auto">
                        {stages.map((stage, i) => {
                            const isCurrent = stage.id === deal.stageId
                            const isPast = stage.sortOrder < (deal.stage?.sortOrder || 0)
                            const canClick = canUpdate && !isCurrent && !moving
                            return (
                                <div key={stage.id} className="flex items-center gap-1 flex-shrink-0">
                                    <button
                                        type="button"
                                        disabled={!canClick}
                                        onClick={() => {
                                            if (!canClick) return
                                            setStageToMove({ id: stage.id, name: stage.name })
                                        }}
                                        className={`px-3 py-1 rounded text-xs font-medium transition-all ${isCurrent
                                            ? 'ring-2 ring-secondary ring-offset-1 cursor-default'
                                            : canClick
                                                ? `cursor-pointer ${isPast ? 'opacity-60 hover:opacity-100' : 'opacity-40 hover:opacity-100'}`
                                                : isPast
                                                    ? 'opacity-60 cursor-not-allowed'
                                                    : 'opacity-40 cursor-not-allowed'
                                            }`}
                                        style={{
                                            backgroundColor: stage.color || '#888',
                                            color: '#fff',
                                        }}
                                    >
                                        {stage.name}
                                    </button>
                                    {i < stages.length - 1 && (
                                        <MdArrowForward size={12} className="opacity-30 flex-shrink-0" />
                                    )}
                                </div>
                            )
                        })}
                    </div>
                )}

                {/* Tabs */}
                <div role="tablist" className="tabs tabs-bordered">
                    {([
                        ['details', 'Detalhes'],
                        ['appointments', 'Agendamentos'],
                        ['billings', 'Cobranças'],
                        ['lead', 'Lead'],
                        ['conversations', 'Conversas'],
                        ['history', 'Histórico'],
                        ['tasks', 'Tarefas'],
                    ] as [TabKey, string][]).map(([key, label]) => (
                        <button
                            key={key}
                            role="tab"
                            className={`tab ${activeTab === key ? 'tab-active' : ''}`}
                            onClick={() => setActiveTab(key)}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                {/* Details Tab */}
                {activeTab === 'details' && (
                    <div className="space-y-6">
                        {/* Deal Info */}
                        <div className="card bg-base-100 shadow-sm">
                            <div className="card-body">
                                <h3 className="card-title text-base">Informações da Oportunidade</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                                    <InfoRow label="Título" value={deal.title} />
                                    <InfoRow label="Status" value={DEAL_STATUS_LABELS[deal.status]} />
                                    <InfoRow label="Funil" value={deal.pipeline?.name || '-'} />
                                    <InfoRow label="Etapa" value={deal.stage?.name || '-'} />
                                    <InfoRow label="Valor Total" value={formatBRL(deal.totalValue || 0)} />
                                    <InfoRow label="Responsável" value={deal.assignedUser?.name || 'Não atribuído'} />
                                    <InfoRow
                                        label="Previsão de Fechamento"
                                        value={
                                            deal.expectedCloseDate
                                                ? format(new Date(deal.expectedCloseDate), 'dd/MM/yyyy', { locale: ptBR })
                                                : '-'
                                        }
                                    />
                                    <InfoRow
                                        label="Criado em"
                                        value={format(new Date(deal.createdAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                                    />
                                    {deal.closedAt && (
                                        <InfoRow
                                            label="Fechado em"
                                            value={format(new Date(deal.closedAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                                        />
                                    )}
                                    {deal.winLossNotes && <InfoRow label="Observação de Fechamento" value={deal.winLossNotes} />}
                                    {deal.description && (
                                        <div className="col-span-full">
                                            <InfoRow label="Descrição" value={deal.description} />
                                        </div>
                                    )}
                                    {deal.tags && deal.tags.length > 0 && (
                                        <div className="col-span-full flex items-center gap-2">
                                            <span className="text-sm opacity-70">Tags:</span>
                                            {deal.tags.map((tag, i) => (
                                                <span key={i} className="badge badge-sm badge-outline">{tag}</span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Items */}
                        <div className="card bg-base-100 shadow-sm">
                            <div className="card-body">
                                <div className="flex items-center justify-between">
                                    <h3 className="card-title text-base">
                                        <MdAttachMoney size={18} /> Itens
                                    </h3>
                                    {canUpdate && deal.status === 'OPEN' && (
                                        <button
                                            className="btn btn-sm btn-ghost"
                                            onClick={() => {
                                                setItemForm({ name: '', unitPrice: 0, quantity: 1, discount: 0 })
                                                setShowAddItemModal(true)
                                            }}
                                        >
                                            <MdAdd size={16} /> Adicionar
                                        </button>
                                    )}
                                </div>
                                {!deal.items || deal.items.length === 0 ? (
                                    <div className="text-center py-6 opacity-50 text-sm">
                                        Nenhum item adicionado
                                    </div>
                                ) : (
                                    <div className="overflow-x-auto mt-2">
                                        <table className="table table-sm">
                                            <thead>
                                                <tr>
                                                    <th>Nome</th>
                                                    <th className="text-right">Qtd</th>
                                                    <th className="text-right">Preço Unit.</th>
                                                    <th className="text-right">Desconto</th>
                                                    <th className="text-right">Total</th>
                                                    {canUpdate && deal.status === 'OPEN' && <th />}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {deal.items.map(item => (
                                                    <tr key={item.id}>
                                                        <td>{item.name}</td>
                                                        <td className="text-right">{item.quantity}</td>
                                                        <td className="text-right">{formatBRL(item.unitPrice)}</td>
                                                        <td className="text-right">{formatBRL(item.discount)}</td>
                                                        <td className="text-right font-medium">{formatBRL(item.total)}</td>
                                                        {canUpdate && deal.status === 'OPEN' && (
                                                            <td>
                                                                <button
                                                                    className="btn btn-ghost btn-xs text-error"
                                                                    onClick={() => {
                                                                        setItemToRemove(item.id)
                                                                        setShowRemoveItemModal(true)
                                                                    }}
                                                                >
                                                                    <MdDelete size={14} />
                                                                </button>
                                                            </td>
                                                        )}
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot>
                                                <tr>
                                                    <td colSpan={4} className="text-right font-bold">Total</td>
                                                    <td className="text-right font-bold">{formatBRL(deal.totalValue || itemsTotal)}</td>
                                                    {canUpdate && deal.status === 'OPEN' && <td />}
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Appointments Tab */}
                {activeTab === 'appointments' && (
                    <div className="space-y-4">
                        {/* Consultas */}
                        {deal.consultations && deal.consultations.length > 0 && (
                            <div className="card bg-base-100 shadow-sm">
                                <div className="card-body">
                                    <h3 className="card-title text-base flex items-center gap-2">
                                        <MdMedicalServices className="text-secondary" />
                                        Consultas ({deal.consultations.length})
                                    </h3>
                                    <div className="overflow-x-auto mt-2">
                                        <table className="table table-sm">
                                            <thead>
                                                <tr>
                                                    <th>Titulo</th>
                                                    <th>Paciente</th>
                                                    <th>Data</th>
                                                    <th>Status</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {deal.consultations.map((c) => (
                                                    <tr key={c.id}>
                                                        <td className="font-medium">{c.title || `Consulta #${c.id}`}</td>
                                                        <td>{c.client?.name || '-'}</td>
                                                        <td>{c.scheduledAt ? format(new Date(c.scheduledAt), "dd/MM/yyyy HH:mm", { locale: ptBR }) : '-'}</td>
                                                        <td><span className={`badge badge-sm ${STATUS_COLORS[c.status] || 'badge-ghost'}`}>{APPOINTMENT_STATUS_LABELS[c.status] || c.status}</span></td>
                                                        <td>
                                                            <button onClick={() => navigate(`/bookings/consultations/${c.id}`)} className="btn btn-xs btn-outline btn-secondary gap-1">
                                                                <CgEye size={14} /> Ver Detalhes
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Exames */}
                        {deal.exams && deal.exams.length > 0 && (
                            <div className="card bg-base-100 shadow-sm">
                                <div className="card-body">
                                    <h3 className="card-title text-base flex items-center gap-2">
                                        <MdBiotech className="text-info" />
                                        Exames ({deal.exams.length})
                                    </h3>
                                    <div className="overflow-x-auto mt-2">
                                        <table className="table table-sm">
                                            <thead>
                                                <tr>
                                                    <th>Titulo</th>
                                                    <th>Paciente</th>
                                                    <th>Data</th>
                                                    <th>Status</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {deal.exams.map((ex) => (
                                                    <tr key={ex.id}>
                                                        <td className="font-medium">{ex.title || `Exame #${ex.id}`}</td>
                                                        <td>{ex.client?.name || '-'}</td>
                                                        <td>{ex.scheduledAt ? format(new Date(ex.scheduledAt), "dd/MM/yyyy HH:mm", { locale: ptBR }) : '-'}</td>
                                                        <td><span className={`badge badge-sm ${STATUS_COLORS[ex.status] || 'badge-ghost'}`}>{APPOINTMENT_STATUS_LABELS[ex.status] || ex.status}</span></td>
                                                        <td>
                                                            <button onClick={() => navigate(`/bookings/exams/${ex.id}`)} className="btn btn-xs btn-outline btn-secondary gap-1">
                                                                <CgEye size={14} /> Ver Detalhes
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Pedidos/Procedimentos do negócio */}
                        {deal.orders && deal.orders.length > 0 && (
                            <div className="card bg-base-100 shadow-sm">
                                <div className="card-body">
                                    <h3 className="card-title text-base flex items-center gap-2">
                                        <MdMedicalServices className="text-secondary" />
                                        {serviceLabels.Orders} ({deal.orders.length})
                                    </h3>
                                    <div className="overflow-x-auto mt-2">
                                        <table className="table table-sm">
                                            <thead>
                                                <tr>
                                                    <th>Nome</th>
                                                    <th>Paciente</th>
                                                    <th>Data</th>
                                                    <th>Status</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {deal.orders.map((p) => (
                                                    <tr key={p.id}>
                                                        <td className="font-medium">{p.catalog?.name || `${serviceLabels.Order} #${p.id}`}</td>
                                                        <td>{p.client?.name || '-'}</td>
                                                        <td>{p.scheduledAt ? format(new Date(p.scheduledAt), "dd/MM/yyyy HH:mm", { locale: ptBR }) : '-'}</td>
                                                        <td><span className={`badge badge-sm ${STATUS_COLORS[p.status] || 'badge-ghost'}`}>{APPOINTMENT_STATUS_LABELS[p.status] || p.status}</span></td>
                                                        <td>
                                                            <button onClick={() => navigate(`/bookings/orders/${p.id}`)} className="btn btn-xs btn-outline btn-secondary gap-1">
                                                                <CgEye size={14} /> Ver Detalhes
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Teleconsultas */}
                        {deal.teleconsultations && deal.teleconsultations.length > 0 && (
                            <div className="card bg-base-100 shadow-sm">
                                <div className="card-body">
                                    <h3 className="card-title text-base flex items-center gap-2">
                                        <MdVideocam className="text-accent" />
                                        Teleconsultas ({deal.teleconsultations.length})
                                    </h3>
                                    <div className="overflow-x-auto mt-2">
                                        <table className="table table-sm">
                                            <thead>
                                                <tr>
                                                    <th>Paciente</th>
                                                    <th>Data</th>
                                                    <th>Status</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {deal.teleconsultations.map((tc) => (
                                                    <tr key={tc.id}>
                                                        <td className="font-medium">{tc.client?.name || '-'}</td>
                                                        <td>{tc.scheduledAt ? format(new Date(tc.scheduledAt), "dd/MM/yyyy HH:mm", { locale: ptBR }) : '-'}</td>
                                                        <td><span className={`badge badge-sm ${STATUS_COLORS[tc.status] || 'badge-ghost'}`}>{APPOINTMENT_STATUS_LABELS[tc.status] || tc.status}</span></td>
                                                        <td>
                                                            <button onClick={() => navigate(`/bookings/teleconsultations/${tc.id}`)} className="btn btn-xs btn-outline btn-secondary gap-1">
                                                                <CgEye size={14} /> Ver Detalhes
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Nenhum agendamento */}
                        {(!deal.consultations?.length && !deal.exams?.length && !deal.orders?.length && !deal.teleconsultations?.length) && (
                            <div className="card bg-base-100 shadow-sm">
                                <div className="card-body text-center text-base-content/50">
                                    Nenhum agendamento vinculado a esta oportunidade.
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Billings Tab */}
                {activeTab === 'billings' && (
                    <div className="space-y-4">
                        {deal.billings && deal.billings.length > 0 ? (
                            <div className="card bg-base-100 shadow-sm">
                                <div className="card-body">
                                    <h3 className="card-title text-base flex items-center gap-2">
                                        <MdReceipt className="text-warning" />
                                        Cobranças ({deal.billings.length})
                                    </h3>
                                    <div className="overflow-x-auto mt-2">
                                        <table className="table table-sm">
                                            <thead>
                                                <tr>
                                                    <th>Descrição</th>
                                                    <th>Valor</th>
                                                    <th>Vencimento</th>
                                                    <th>Pago em</th>
                                                    <th>Status</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {deal.billings.map((b) => (
                                                    <tr key={b.id}>
                                                        <td className="font-medium">{b.description || `Cobrança #${b.id}`}</td>
                                                        <td>{formatBRL(b.amount)}</td>
                                                        <td>{b.dueDate ? format(new Date(b.dueDate), "dd/MM/yyyy", { locale: ptBR }) : '-'}</td>
                                                        <td>{b.paidAt ? format(new Date(b.paidAt), "dd/MM/yyyy", { locale: ptBR }) : '-'}</td>
                                                        <td><span className={`badge badge-sm ${STATUS_COLORS[b.status] || 'badge-ghost'}`}>{APPOINTMENT_STATUS_LABELS[b.status] || b.status}</span></td>
                                                        <td>
                                                            <button onClick={() => navigate(`/financial/billings/${b.id}`)} className="btn btn-xs btn-outline btn-secondary gap-1">
                                                                <CgEye size={14} /> Ver Detalhes
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="card bg-base-100 shadow-sm">
                                <div className="card-body text-center text-base-content/50">
                                    Nenhuma cobrança vinculada a esta oportunidade.
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Lead Tab */}
                {activeTab === 'lead' && (
                    <div className="card bg-base-100 shadow-sm">
                        <div className="card-body">
                            {deal.lead ? (
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <h3 className="card-title text-base">
                                            <MdPerson size={18} /> {deal.lead.name}
                                        </h3>
                                        <button
                                            className="btn btn-sm btn-outline"
                                            onClick={() => navigate(`/sales/leads/${deal.leadId}`)}
                                        >
                                            Ver Detalhe
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <InfoRow label="Telefone" value={deal.lead.phoneNumber} />
                                        <InfoRow label="E-mail" value={deal.lead.email || '-'} />
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-8 opacity-50">
                                    <MdPerson size={40} className="mx-auto mb-2" />
                                    <p>Nenhum lead vinculado</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* History Tab */}
                {activeTab === 'history' && (
                    <div className="card bg-base-100 shadow-sm">
                        <div className="card-body">
                            <h3 className="card-title text-base">
                                <MdTimeline size={18} /> Transições de Etapa
                            </h3>
                            {!deal.transitions || deal.transitions.length === 0 ? (
                                <div className="text-center py-8 opacity-50 text-sm">
                                    Nenhuma transição registrada
                                </div>
                            ) : (
                                <ul className="timeline timeline-vertical timeline-compact mt-4">
                                    {deal.transitions.map((t, i) => (
                                        <li key={t.id}>
                                            {i > 0 && <hr />}
                                            <div className="timeline-start text-xs opacity-60 whitespace-nowrap">
                                                {format(new Date(t.createdAt), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                                            </div>
                                            <div className="timeline-middle">
                                                <MdArrowRightAlt size={20} className="text-secondary" />
                                            </div>
                                            <div className="timeline-end timeline-box">
                                                <div className="flex items-center gap-2 text-sm">
                                                    <span
                                                        className="badge badge-sm"
                                                        style={{
                                                            backgroundColor: t.fromStage ? '#888' : undefined,
                                                            color: t.fromStage ? '#fff' : undefined,
                                                        }}
                                                    >
                                                        {t.fromStage?.name || 'Início'}
                                                    </span>
                                                    <MdArrowRightAlt size={14} />
                                                    <span
                                                        className="badge badge-sm"
                                                        style={{
                                                            backgroundColor: '#4f46e5',
                                                            color: '#fff',
                                                        }}
                                                    >
                                                        {t.toStage?.name || '-'}
                                                    </span>
                                                </div>
                                                {t.reason && (
                                                    <p className="text-xs opacity-60 mt-1">Motivo: {t.reason}</p>
                                                )}
                                                {t.user && (
                                                    <p className="text-xs opacity-50 mt-1">por {t.user.name}</p>
                                                )}
                                                {t.duration != null && t.duration > 0 && (
                                                    <p className="text-xs opacity-50">
                                                        Duração na etapa: {Math.round(t.duration / 60)} hora(s)
                                                    </p>
                                                )}
                                            </div>
                                            {i < deal.transitions!.length - 1 && <hr />}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                )}

                {/* Conversa da oportunidade, embutida como no detalhe do lead. A da própria
                    oportunidade (`chatId`) vem primeiro; sem ela, as do lead. O detalhe não
                    traz `deal.chat`: ler só ele deixava esta aba sempre vazia. */}
                {activeTab === 'conversations' &&
                    (() => {
                        const leadChats: any[] = (deal as any)?.lead?.chats ?? []
                        const chatIds = Array.from(
                            new Set([(deal as any)?.chatId, ...leadChats.map(c => c.id)].filter(Boolean) as number[])
                        )
                        if (chatIds.length === 0) {
                            return (
                                <div className="text-center py-12 opacity-50">
                                    <MdChat size={48} className="mx-auto mb-2" />
                                    <p>Nenhuma conversa vinculada a esta oportunidade</p>
                                </div>
                            )
                        }
                        const openChatId = selectedChatId && chatIds.includes(selectedChatId) ? selectedChatId : chatIds[0]
                        return (
                            <div className="space-y-3">
                                {chatIds.length > 1 && (
                                    <div className="flex flex-wrap gap-2">
                                        {chatIds.map(chatId => (
                                            <button
                                                key={chatId}
                                                className={`btn btn-sm ${openChatId === chatId ? 'btn-secondary' : 'btn-ghost border border-base-300'}`}
                                                onClick={() => setSelectedChatId(chatId)}
                                            >
                                                <ChatChannelIcon
                                                    channel={leadChats.find(c => c.id === chatId)?.channel}
                                                    colored={openChatId !== chatId}
                                                />
                                                Conversa #{chatId}
                                                {chatId === (deal as any)?.chatId && (
                                                    <span className="text-xs opacity-60">desta oportunidade</span>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                )}
                                <div className="h-[70vh] min-h-[480px] rounded-xl border border-base-300/60 overflow-hidden flex flex-col bg-base-100">
                                    <AtendimentoChatPage
                                        key={openChatId}
                                        chatIdOverride={openChatId}
                                        // Sem isto, a seta do chat navegava para a lista de atendimentos
                                        onBack={() => setActiveTab('details')}
                                    />
                                </div>
                            </div>
                        )
                    })()}

                {activeTab === 'tasks' && deal && (
                    <SalesTasksPanel dealId={deal.id} leadId={deal.leadId} defaultAssigneeId={deal.assignedUserId ?? null} />
                )}

                {/* Close Deal Modal */}
                {showCloseModal && (
                    <dialog className="modal modal-open">
                        <div className="modal-box">
                            <h3 className="font-bold text-lg mb-4">Fechar Oportunidade</h3>
                            <div className="space-y-4">
                                <div className="form-control">
                                    <label className="label"><span className="label-text">Resultado *</span></label>
                                    <select
                                        className="select select-bordered"
                                        value={closeForm.status}
                                        onChange={e =>
                                            setCloseForm({
                                                ...closeForm,
                                                status: e.target.value as CloseDealData['status'],
                                            })
                                        }
                                    >
                                        <option value="WON">Ganho</option>
                                        <option value="LOST">Perdido</option>
                                        <option value="ABANDONED">Abandonado</option>
                                    </select>
                                </div>
                                <div className="form-control">
                                    <label className="label"><span className="label-text">Observações</span></label>
                                    <textarea
                                        className="textarea textarea-bordered"
                                        rows={3}
                                        value={closeForm.winLossNotes || ''}
                                        onChange={e =>
                                            setCloseForm({ ...closeForm, winLossNotes: e.target.value })
                                        }
                                        placeholder="Observações sobre o fechamento..."
                                    />
                                </div>
                            </div>
                            <div className="modal-action">
                                <button className="btn btn-ghost" onClick={() => setShowCloseModal(false)}>
                                    Cancelar
                                </button>
                                <button
                                    className={`btn ${closeForm.status === 'WON' ? 'btn-success' : closeForm.status === 'LOST' ? 'btn-error' : 'btn-warning'}`}
                                    onClick={handleClose}
                                    disabled={closing}
                                >
                                    {closing && <span className="loading loading-spinner loading-sm" />}
                                    Fechar como {closeForm.status === 'WON' ? 'Ganho' : closeForm.status === 'LOST' ? 'Perdido' : 'Abandonado'}
                                </button>
                            </div>
                        </div>
                        <form method="dialog" className="modal-backdrop">
                            <button onClick={() => setShowCloseModal(false)}>close</button>
                        </form>
                    </dialog>
                )}

                {/* Edit Modal */}
                {showEditModal && (
                    <dialog className="modal modal-open">
                        <div className="modal-box max-w-lg">
                            <h3 className="font-bold text-lg mb-4">Editar Oportunidade</h3>
                            <div className="space-y-4">
                                <div className="form-control">
                                    <label className="label"><span className="label-text">Título</span></label>
                                    <input
                                        type="text"
                                        className="input input-bordered"
                                        value={editForm.title || ''}
                                        onChange={e => setEditForm({ ...editForm, title: e.target.value })}
                                    />
                                </div>
                                <div className="form-control">
                                    <label className="label"><span className="label-text">Descrição</span></label>
                                    <textarea
                                        className="textarea textarea-bordered"
                                        rows={3}
                                        value={editForm.description || ''}
                                        onChange={e => setEditForm({ ...editForm, description: e.target.value })}
                                    />
                                </div>
                                <div className="form-control">
                                    <label className="label"><span className="label-text">Previsão de Fechamento</span></label>
                                    <input
                                        type="date"
                                        className="input input-bordered"
                                        value={editForm.expectedCloseDate || ''}
                                        onChange={e =>
                                            setEditForm({ ...editForm, expectedCloseDate: e.target.value || undefined })
                                        }
                                    />
                                </div>
                                <div className="form-control">
                                    <label className="label"><span className="label-text">Tags (separadas por vírgula)</span></label>
                                    <input
                                        type="text"
                                        className="input input-bordered"
                                        value={(editForm.tags || []).join(', ')}
                                        onChange={e =>
                                            setEditForm({
                                                ...editForm,
                                                tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean),
                                            })
                                        }
                                    />
                                </div>
                            </div>
                            <div className="modal-action">
                                <button className="btn btn-ghost" onClick={() => setShowEditModal(false)}>
                                    Cancelar
                                </button>
                                <button className="btn btn-secondary" onClick={handleEdit} disabled={saving}>
                                    {saving && <span className="loading loading-spinner loading-sm" />}
                                    Salvar
                                </button>
                            </div>
                        </div>
                        <form method="dialog" className="modal-backdrop">
                            <button onClick={() => setShowEditModal(false)}>close</button>
                        </form>
                    </dialog>
                )}

                {/* Add Item Modal */}
                {showAddItemModal && (
                    <dialog className="modal modal-open">
                        <div className="modal-box !max-w-lg">
                            <h3 className="font-bold text-lg mb-4">Adicionar Item</h3>
                            <DealItemEditor
                                items={[itemForm]}
                                onChange={(updated) => setItemForm(updated[0] || { name: '', unitPrice: 0 })}
                            />
                            <div className="modal-action">
                                <button className="btn btn-ghost" onClick={() => { setShowAddItemModal(false); resetItemForm() }}>
                                    Cancelar
                                </button>
                                <button className="btn btn-secondary" onClick={handleAddItem} disabled={addingItem}>
                                    {addingItem && <span className="loading loading-spinner loading-sm" />}
                                    Adicionar
                                </button>
                            </div>
                        </div>
                        <form method="dialog" className="modal-backdrop">
                            <button onClick={() => { setShowAddItemModal(false); resetItemForm() }}>close</button>
                        </form>
                    </dialog>
                )}

                {/* Delete Confirmation */}
                <ConfirmModal
                    isOpen={showDeleteModal}
                    onClose={() => setShowDeleteModal(false)}
                    onConfirm={handleDelete}
                    title="Remover Oportunidade"
                    message="Tem certeza que deseja remover esta oportunidade? Esta ação não pode ser desfeita."
                    loading={deleting}
                    confirmButtonText="Remover"
                    confirmButtonClass="btn-error"
                />

                {/* Remove Item Confirmation */}
                <ConfirmModal
                    isOpen={showRemoveItemModal}
                    onClose={() => {
                        setShowRemoveItemModal(false)
                        setItemToRemove(null)
                    }}
                    onConfirm={handleRemoveItem}
                    title="Remover Item"
                    message="Tem certeza que deseja remover este item da oportunidade?"
                    loading={removingItem}
                    confirmButtonText="Remover"
                    confirmButtonClass="btn-error"
                />

                {/* Move Stage Confirmation */}
                <ConfirmModal
                    isOpen={!!stageToMove}
                    onClose={() => setStageToMove(null)}
                    onConfirm={handleMoveStage}
                    title="Mover Etapa"
                    message={
                        stageToMove
                            ? `Deseja mover esta oportunidade para a etapa "${stageToMove.name}"?`
                            : ''
                    }
                    loading={moving}
                    confirmButtonText="Mover"
                    confirmButtonClass="btn-secondary"
                />
            </div>
        </ChatPopupProvider>
    )
}

function InfoRow({ label, value }: { label: string; value: string }) {
    return (
        <div className="flex items-center justify-between">
            <span className="text-sm opacity-70">{label}</span>
            <span className="text-sm font-medium text-right max-w-[60%] break-words">{value}</span>
        </div>
    )
}
